import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { RootState } from 'store';

import { OrderPageParams } from 'order/interfaces/OrderPageParams';
import { OrderTrackingResponse } from 'order/wizard/orderTracking/interface/OrderTracking';
import { getOrderTracking } from 'order/wizard/orderTracking/store/orderTrackingActions';
import { BoxShadowCSS } from 'shared/config/GlobalStyles';
import { Divider, Spacer } from 'shared/components/Layout';
import { H2, PMedium, PXSmall } from 'shared/components/Typography';
import { Wrapper } from 'shared/components/Wrapper';
import { nevada } from 'shared/config/Colors';
import { useAppDispatch } from 'shared/hooks/useAppDispatch';
import EmptyState from 'shared/components/EmptyState';
import Loader from 'shared/components/Loader';
import UtilService from 'shared/services/util.service';

import { ReactComponent as SyncIcon } from 'assets/icons/sync.svg';
import { ReactComponent as TruckIcon } from 'assets/icons/truck.svg';

import { TrackingGrid, TrackingRow } from './components/TrackingRow';

const TrackingCard = styled.div`
  ${BoxShadowCSS};
  background-color: ${({ theme }) => theme.white};
  border-radius: 16px;
`;

const HeaderLabel = styled(PXSmall)`
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const CardFooter = styled(Wrapper)`
  border-top: 1px solid ${({ theme }) => theme.mischka};
  padding: 12px 24px;
`;

const OrderTracking = () => {
  const dispatch = useAppDispatch();
  const { orderId } = useParams<OrderPageParams>();

  const [orderTracking, setOrderTracking] =
    useState<OrderTrackingResponse | null>(null);

  const [isLoading, setIsLoading] = useState(true);

  // Net/sales aid orders have no styles, BE returns all their trackings without one.
  const withStyle = useSelector(
    (state: RootState) =>
      !UtilService.isSalesAids(state.orderReducer.order?.type)
  );

  useEffect(() => {
    setIsLoading(true);

    dispatch(
      getOrderTracking(
        orderId,
        (response) => {
          setOrderTracking(response ?? null);
          setIsLoading(false);
        },
        () => {
          toast.error("Tracking information couldn't be loaded.");
          setIsLoading(false);
        }
      )
    );
  }, [orderId]);

  const headerLabels = [
    ...(withStyle ? ['Style'] : []),
    'Tracking number',
    'Delivery',
    'Status',
  ];

  return (
    <>
      <H2>Tracking</H2>
      <Spacer h="6px" />
      <PMedium color={nevada}>
        {withStyle
          ? 'FedEx tracking numbers for each style on this order.'
          : 'FedEx tracking numbers for this order.'}
      </PMedium>
      <Spacer h="16px" />
      <Divider />
      <Spacer h="24px" />

      {isLoading && <Loader size={50} />}

      {!isLoading && orderTracking && !orderTracking.trackings.length && (
        <EmptyState
          title="No tracking numbers yet!"
          icon={<TruckIcon />}
          iconSize={80}
          marginTop={0}
        />
      )}

      {!isLoading && !!orderTracking?.trackings.length && (
        <TrackingCard>
          <TrackingGrid withStyle={withStyle}>
            {headerLabels.map((label) => (
              <HeaderLabel key={label}>{label}</HeaderLabel>
            ))}
          </TrackingGrid>

          {orderTracking.trackings.map((styleTracking) => (
            <TrackingRow
              key={styleTracking.style?.id ?? 'without-style'}
              styleTracking={styleTracking}
              withStyle={withStyle}
            />
          ))}

          {!withStyle && (
            <CardFooter flex middle gap={6} color={nevada}>
              <SyncIcon width={12} height={12} />
              <PXSmall color={nevada}>
                Tracking numbers synced from shipping · Status and history from
                FedEx
              </PXSmall>
            </CardFooter>
          )}
        </TrackingCard>
      )}
    </>
  );
};

export default OrderTracking;
